import { DualPosition } from "appworks/graphics/pixi/dual-position";
import { CenterPivot, PIXIElement } from "appworks/graphics/pixi/group";
import { SpineContainer } from "appworks/graphics/pixi/spine-container";
import { SpineService } from "appworks/graphics/spine/spine-service";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Point } from "appworks/utils/geom/point";
import { gameLayers } from "game-layers";
import { SlingoTicketMatrixComponent } from "slingo/components/slingo-ticket-matrix-component";
import { SlingoSoundEvent } from "slingo/sound/slingo-sound-events";

export class FowlTicketMatrixComponent extends SlingoTicketMatrixComponent {
    public setHighlightAlpha(alpha: number) {
        this.highlights.forEach((col: PIXIElement[]) =>
            col.forEach(highlight => {
                highlight.alpha = alpha;
            })
        );
    }

    protected getDabSprite() {
        const spineService = Services.get(SpineService);
        const spine = spineService.createSpineContainer("dab", gameLayers.TicketMatrix.getPosition("dab"));
        return CenterPivot(spine);
    }

    protected getDabAnimationContract(dab: SpineContainer, instant: boolean): Contract<void> {
        return dab.playOnce("dab", false, instant ? 100 : 1.5);
    }

    protected getDabPosition(xyPos: Point): DualPosition {
        const dabPos = gameLayers.TicketMatrix.getPosition("dab");
        const basePos = gameLayers.TicketMatrix.getPosition("symbol_0_0");
        const symbolPos = gameLayers.TicketMatrix.getPosition(`symbol_${xyPos.x}_${xyPos.y}`);

        const offset = symbolPos.subtractPosition(basePos);

        return dabPos.addPosition(offset);
    }

    protected animateLine(pattern: string, index: number): Contract {
        const values = this.getValuesForWinlinePattern(pattern);
        const positions = values.map(val => this.getSymbolPositionFromValue(val));

        const isVertical = positions.every(pos => pos.x === positions[0].x);
        positions.sort((a, b) => (isVertical ? a.y - b.y : a.x - b.x));

        const delay = 75;
        const contracts = positions.map((pos: Point, index: number) => {
            return () =>
                Contract.getDelayedContract(delay * index, () => {
                    const dab = this.dabs[pos.x][pos.y] as SpineContainer;
                    if (!dab) {
                        return Contract.empty();
                    }
                    this.layer.add(dab); // bring to top so it doesn't pulse under other dabs
                    return dab.playOnce("pulse");
                });
        });

        contracts.push(() => Contract.wrap(() => Services.get(SoundService).event((SlingoSoundEvent as any).slingo_animate_line)));
        contracts.push(() => Contract.wrap(() => Services.get(SoundService).event((SlingoSoundEvent as any).slingo_animate_line_N, index.toString())));

        return new Parallel(contracts);
    }

    protected getDabAnimPatternContracts(pattern: number[][], subAnimDuration = 125, spacingMs = 65) {
        const patternContract: Array<Array<() => Contract>> = [];
        for (let y = 0; y < pattern.length; y++) {
            for (let x = 0; x < pattern[y].length; x++) {
                const dab = this.dabs[x][y] as SpineContainer;
                if (patternContract[pattern[x][y]]) {
                    patternContract[pattern[x][y]].push(() => dab.playOnce("pulse"));
                } else {
                    patternContract[pattern[x][y]] = [() => dab.playOnce("pulse")];
                }
            }
        }
        return new Parallel(patternContract.map((subAnims, i) => () => Contract.getDelayedContract(spacingMs * i, () => new Parallel(subAnims))));
    }
}
